import React from 'react';

export interface ProductCellProps {
  image?: string;
  fallbackImage?: string;
  name: string;
  subtitle?: string;
  sku?: string;
  className?: string;
}

/**
 * ProductCell Component
 * Renders a crisp 40-44px product thumbnail with rounded corners and subtle border,
 * paired with a clean product title and optional subtitle/category/SKU.
 */
export const ProductCell: React.FC<ProductCellProps> = ({
  image,
  fallbackImage,
  name,
  subtitle,
  sku,
  className = '',
}) => {
  const imgSrc = image || fallbackImage;

  return (
    <div className={`flex items-center gap-3.5 h-full py-1.5 overflow-hidden ${className}`}>
      {imgSrc ? (
        <img
          src={imgSrc}
          alt={name}
          className="w-10 h-10 sm:w-11 sm:h-11 rounded-lg object-cover border border-slate-200/90 shrink-0 shadow-2xs transition-transform hover:scale-105"
          onError={(e) => {
            if (fallbackImage && e.currentTarget.src !== fallbackImage) {
              e.currentTarget.src = fallbackImage;
            }
          }}
        />
      ) : (
        <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-lg bg-slate-100 text-slate-400 flex items-center justify-center font-bold text-xs shrink-0 border border-slate-200">
          IMG
        </div>
      )}

      <div className="flex flex-col justify-center overflow-hidden min-w-0">
        <span
          className="font-semibold text-slate-900 text-sm truncate hover:text-[#1AA7DE] transition-colors leading-snug"
          title={name}
        >
          {name}
        </span>
        {(subtitle || sku) && (
          <span
            className="text-[11px] font-medium text-slate-400 truncate mt-0.5"
            title={subtitle || sku}
          >
            {sku ? `SKU: ${sku}` : subtitle}
          </span>
        )}
      </div>
    </div>
  );
};

export default ProductCell;
